import { createAction } from "@reduxjs/toolkit";
import { createRsaaAction } from "../../components/utilities/ReduxUtils";
import type {
    AgentUiContext,
    ChemistryAgentContextResponse,
    ChemistryAgentUiContextRequest,
    LlmMessage,
    LlmStreamUpdate,
    WebSocketLlmConversationMessagePayload,
    WebSocketLlmConversationMessageStreamPayload,
    WebSocketLlmConversationMessageSuccess,
    WebSocketLlmConversationStartSuccess,
    WebSocketLlmConversationStopSuccess,
} from "../../data/models/schemas";
import { createSocketAction } from "../middlewares/websocket";
import { refreshLicenseInfoMeta } from "../utils/meteringMeta";

export const LLM_CONVERSATION_START = "LLM_CONVERSATION_START";
export const LLM_CONVERSATION_STOP = "LLM_CONVERSATION_STOP";
export const LLM_CONVERSATION_MESSAGE = "LLM_CONVERSATION_MESSAGE";
export const LLM_CONVERSATION_MESSAGE_STREAM =
    "LLM_CONVERSATION_MESSAGE_STREAM";
export const LLM_CONTEXT_UPDATE = "LLM_CONTEXT_UPDATE";
export const LLM_LOCAL_MESSAGE_SUBMITTED = "LLM_LOCAL_MESSAGE_SUBMITTED";
export const LLM_OPEN_FEEDBACK_FORM: string = "LLM_OPEN_FEEDBACK_FORM";
export const LLM_CLOSE_FEEDBACK_FORM: string = "LLM_CLOSE_FEEDBACK_FORM";
export const LLM_SET_FEEDBACK: string = "LLM_SET_FEEDBACK";
export const LLM_SUBMIT_FEEDBACK: string = "LLM_SUBMIT_FEEDBACK";

export type IAgentErrorPayload = {
    message?: string;
    code?: string;
};

export type IAgentMessage = WebSocketLlmConversationMessageSuccess &
    LlmMessage &
    Partial<LlmStreamUpdate> & {
        message_id: string;
    };

export const agentConversationStart = createSocketAction<
    void,
    WebSocketLlmConversationStartSuccess
>({
    type: LLM_CONVERSATION_START,
});

export const agentConversationStop = createSocketAction<
    void,
    WebSocketLlmConversationStopSuccess
>({
    type: LLM_CONVERSATION_STOP,
});

export const agentConversationSend = createSocketAction<
    WebSocketLlmConversationMessagePayload,
    IAgentMessage,
    IAgentErrorPayload
>({
    type: LLM_CONVERSATION_MESSAGE,
    meta: refreshLicenseInfoMeta,
});

export const agentConversationStream = createSocketAction<
    WebSocketLlmConversationMessageStreamPayload,
    IAgentMessage,
    IAgentErrorPayload
>({
    type: LLM_CONVERSATION_MESSAGE_STREAM,
    meta: refreshLicenseInfoMeta,
});

export const agentContextUpdate = createRsaaAction<
    ChemistryAgentUiContextRequest,
    ChemistryAgentContextResponse
>({
    path: "llm/chemistry/conversations/:conversationId/context/ui",
    method: "PUT",
    type: LLM_CONTEXT_UPDATE,
});

export const createAgentContextUpdateActionArgs = (
    conversationId: string,
    context: AgentUiContext
) => ({
    pathParams: {
        conversationId,
    },
    body: {
        ui_context: context,
    },
});

export type IAgentFeedbackRequest = {
    conversation_id: string;
    message_id: string;
    messages: IAgentMessage[];
    feedback: string;
};

export const submitAgentFeedback = createRsaaAction<IAgentFeedbackRequest>({
    path: "feedback/agent",
    method: "POST",
    type: LLM_SUBMIT_FEEDBACK,
});

export const openAgentFeedback = createAction(LLM_OPEN_FEEDBACK_FORM);

export const closeAgentFeedback = createAction(LLM_CLOSE_FEEDBACK_FORM);

export const setAgentFeedback = createAction<string>(LLM_SET_FEEDBACK);

export const agentLocalMessageSubmitted = createAction<IAgentMessage>(
    LLM_LOCAL_MESSAGE_SUBMITTED
);
